import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  MessageSquare,
  Send,
  Search,
  Info,
  X,
  Link2,
  GraduationCap,
  CheckCheck,
  Check,
  Mic,
  File,
  ExternalLink,
  Loader2,
  AlertCircle,
  RefreshCw,
  School,
  CreditCard,
  Calendar,
  UserX,
  UserCheck,
  Star,
  ChevronLeft,
  PanelRightClose,
  PanelRightOpen,
  ImageIcon,
  Video,
  Download,
  Bot,
  Tag,
  Plus,
  Trash2,
  Filter,
} from "lucide-react";
import { format, isToday, isYesterday, formatDistanceToNow, isSameDay } from "date-fns";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WaTag {
  id: string;
  name: string;
  color: string;
  createdAt: string;
}

interface WaMessage {
  id: string;
  userId?: string | null;
  recipientPhone: string;
  direction: "inbound" | "outbound";
  status: string;
  messageType: string;
  messageBody: string | null;
  mediaUrl: string | null;
  mediaContentType: string | null;
  createdAt: string;
}

interface Conversation {
  phone: string;
  displayName: string | null;
  lastMessage: WaMessage;
  unreadCount: number;
}

interface ContactInfo {
  found: boolean;
  userId?: string;
  studentFirstName?: string;
  studentLastName?: string;
  grade?: number;
  school?: string | null;
  parentFirstName?: string | null;
  parentLastName?: string | null;
  parentEmail?: string | null;
  subscriptionStatus?: string;
  subscriptionPlan?: string | null;
  subscriptionEndDate?: string | null;
  subscriptionExpired?: boolean;
  cancelAtPeriodEnd?: boolean;
  isActive?: boolean;
  lastActiveAt?: string | null;
  daysSinceActive?: number | null;
  isTestAccount?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mediaProxyUrl(url: string) {
  return `/api/admin/whatsapp/media-proxy?url=${encodeURIComponent(url)}`;
}

function getDisplayName(conv: Conversation): string {
  return conv.displayName || formatPhone(conv.phone);
}

function formatPhone(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return `+${d}`;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function dateSeparatorLabel(date: Date): string {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMMM d, yyyy");
}

const subStatusColors: Record<string, string> = {
  active: "text-green-600",
  trial: "text-blue-600",
  expired: "text-red-500",
  cancelled: "text-red-500",
  inactive: "text-muted-foreground",
};

// Tag color → Tailwind badge classes
const TAG_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  slate:  { bg: "bg-slate-100",  text: "text-slate-700",  border: "border-slate-300",  dot: "bg-slate-500"  },
  red:    { bg: "bg-red-100",    text: "text-red-700",    border: "border-red-300",    dot: "bg-red-500"    },
  orange: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", dot: "bg-orange-500" },
  amber:  { bg: "bg-amber-100",  text: "text-amber-700",  border: "border-amber-300",  dot: "bg-amber-500"  },
  green:  { bg: "bg-green-100",  text: "text-green-700",  border: "border-green-300",  dot: "bg-green-500"  },
  teal:   { bg: "bg-teal-100",   text: "text-teal-700",   border: "border-teal-300",   dot: "bg-teal-500"   },
  blue:   { bg: "bg-blue-100",   text: "text-blue-700",   border: "border-blue-300",   dot: "bg-blue-500"   },
  indigo: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", dot: "bg-indigo-500" },
  purple: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", dot: "bg-purple-500" },
  pink:   { bg: "bg-pink-100",   text: "text-pink-700",   border: "border-pink-300",   dot: "bg-pink-500"   },
};

function tagClasses(color: string) {
  return TAG_COLORS[color] ?? TAG_COLORS.slate;
}

const TAG_COLOR_OPTIONS = Object.keys(TAG_COLORS) as string[];

// ─── Sub-components ──────────────────────────────────────────────────────────

function MediaRenderer({
  url,
  contentType,
  outbound,
}: {
  url: string;
  contentType: string | null;
  outbound: boolean;
}) {
  const proxied = mediaProxyUrl(url);
  const type = contentType || "";

  if (type.startsWith("image/")) {
    return (
      <a href={proxied} target="_blank" rel="noreferrer" className="block group">
        <div className="relative overflow-hidden rounded-xl max-w-[280px]">
          <img
            src={proxied}
            alt="Image"
            className="w-full max-h-[280px] object-cover rounded-xl transition-opacity group-hover:opacity-90"
          />
          <div className="absolute inset-0 flex items-end justify-end p-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-black/50 rounded-full p-1.5">
              <ExternalLink className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
        </div>
      </a>
    );
  }

  if (type.startsWith("video/")) {
    return (
      <div className="max-w-[280px] rounded-xl overflow-hidden">
        <video controls className="w-full rounded-xl" preload="metadata">
          <source src={proxied} type={type} />
        </video>
      </div>
    );
  }

  if (type.startsWith("audio/")) {
    return (
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-2xl px-3 py-2.5 min-w-[200px]",
          outbound ? "bg-white/20" : "bg-black/5"
        )}
      >
        <div className={cn("rounded-full p-1.5", outbound ? "bg-white/30" : "bg-primary/10")}>
          <Mic className={cn("w-3.5 h-3.5", outbound ? "text-white" : "text-primary")} />
        </div>
        <audio controls className="h-8 flex-1 min-w-[160px] [&::-webkit-media-controls-panel]:bg-transparent">
          <source src={proxied} type={type} />
        </audio>
      </div>
    );
  }

  // Generic file
  return (
    <a
      href={proxied}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "flex items-center gap-2.5 text-sm rounded-xl px-3 py-2.5 max-w-[220px]",
        outbound ? "bg-white/20 text-white" : "bg-muted text-foreground"
      )}
    >
      <File className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">Download attachment</span>
      <Download className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
    </a>
  );
}

function MessageStatus({ status }: { status: string }) {
  if (status === "read") return <CheckCheck className="w-3 h-3 text-sky-300" />;
  if (status === "delivered") return <CheckCheck className="w-3 h-3 opacity-60" />;
  if (status === "sent") return <Check className="w-3 h-3 opacity-50" />;
  if (status === "failed") return <AlertCircle className="w-3 h-3 text-red-300" />;
  return null;
}

function DateSeparator({ date }: { date: Date }) {
  return (
    <div className="flex items-center gap-3 my-4 px-2">
      <div className="flex-1 h-px bg-border" />
      <span className="text-xs text-muted-foreground bg-background px-2 rounded-full border">
        {dateSeparatorLabel(date)}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

// Human-readable labels for legacy [Template: HX...] log entries
const TEMPLATE_LABELS: Record<string, string> = {
  'HX2909c1b1707002380b7033ca75799906': 'Welcome message sent',
  'HX5aa5445c90601095bc0a8f372ced0ac4': 'Renewal failed notification sent',
  'HX68971e74f4912e6292ba9e22af6daf1a': 'Inactivity reminder sent',
  'HX0bbee631e1165eb8ee08ca267ded2b70': 'Progress update sent',
  'HXb167a44d5ed00101e3b50936711099c1': 'Prize won notification sent',
  'HX4f18224238fcaf5e2df448f2bea16218': 'Failed payment notification sent',
  'HX5d9eb4b8300e919555b60d8b82fcd0c4': 'Cancellation notification sent',
  'HXe2ed86792a7bbd037a327a85aa579772': 'Referral message sent',
  'HX644d8a5f02a5e934c6360b669bd61c4f': 'Weekly report sent',
};

function resolveMessageBody(body: string | null | undefined): { text: string; isTemplateFallback: boolean } {
  if (!body) return { text: '', isTemplateFallback: false };
  const match = body.match(/^\[Template: (HX[a-f0-9]+)\]$/);
  if (match) {
    const label = TEMPLATE_LABELS[match[1]] ?? `Template message sent`;
    return { text: label, isTemplateFallback: true };
  }
  return { text: body, isTemplateFallback: false };
}

function MessageBubble({ msg }: { msg: WaMessage }) {
  const isOut = msg.direction === "outbound";
  const time = format(new Date(msg.createdAt), "h:mm a");
  const hasMedia = !!msg.mediaUrl;
  const { text: resolvedBody, isTemplateFallback } = resolveMessageBody(msg.messageBody);
  const hasText = !!resolvedBody;

  return (
    <div className={cn("flex mb-1.5", isOut ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm",
          isOut
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-muted text-foreground rounded-bl-sm"
        )}
      >
        {hasMedia && (
          <div className={cn("mb-1.5", !hasText && "mb-0")}>
            <MediaRenderer url={msg.mediaUrl!} contentType={msg.mediaContentType} outbound={isOut} />
          </div>
        )}
        {hasText && (
          <p className={cn("text-sm leading-relaxed whitespace-pre-wrap break-words", isTemplateFallback && "italic opacity-70")}>
            {resolvedBody}
          </p>
        )}
        {!hasText && !hasMedia && (
          <p className="text-sm italic opacity-60">[empty message]</p>
        )}
        <div className={cn("flex items-center gap-1 mt-1", isOut ? "justify-end" : "justify-start")}>
          <span className={cn("text-[10px] opacity-70", isOut ? "text-primary-foreground" : "text-muted-foreground")}>
            {time}
          </span>
          {isOut && <MessageStatus status={msg.status} />}
        </div>
      </div>
    </div>
  );
}

function MessageList({ messages }: { messages: WaMessage[] }) {
  const sorted = useMemo(
    () => [...messages].sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      // If within 1 second, inbound always comes before outbound
      if (Math.abs(diff) < 1000 && a.direction !== b.direction) {
        return a.direction === "inbound" ? -1 : 1;
      }
      return diff;
    }),
    [messages]
  );

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
        <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm">No messages yet</p>
      </div>
    );
  }

  const items: React.ReactNode[] = [];
  let lastDate: Date | null = null;

  for (const msg of sorted) {
    const msgDate = new Date(msg.createdAt);
    if (!lastDate || !isSameDay(lastDate, msgDate)) {
      items.push(<DateSeparator key={`sep-${msg.id}`} date={msgDate} />);
      lastDate = msgDate;
    }
    items.push(<MessageBubble key={msg.id} msg={msg} />);
  }

  return <>{items}</>;
}

function ConversationItem({
  conv,
  active,
  onClick,
  tags,
}: {
  conv: Conversation;
  active: boolean;
  onClick: () => void;
  tags: WaTag[];
}) {
  const name = getDisplayName(conv);
  const lastMsg = conv.lastMessage;
  const time = formatDistanceToNow(new Date(lastMsg.createdAt), { addSuffix: false });
  const preview = lastMsg.mediaUrl
    ? lastMsg.mediaContentType?.startsWith("image/")
      ? "📷 Photo"
      : lastMsg.mediaContentType?.startsWith("video/")
      ? "📹 Video"
      : lastMsg.mediaContentType?.startsWith("audio/")
      ? "🎤 Voice message"
      : "📎 Attachment"
    : (() => {
        const { text } = resolveMessageBody(lastMsg.messageBody);
        return text ? (text.length > 45 ? text.slice(0, 45) + "…" : text) : "No messages";
      })();

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left px-3 py-3 flex items-start gap-3 hover:bg-muted/50 transition-colors border-b border-border/40 last:border-b-0",
        active && "bg-primary/5 border-l-2 border-l-primary"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-semibold",
          active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        )}
      >
        {getInitials(name)}
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className={cn("text-sm font-medium truncate", conv.unreadCount > 0 && "font-semibold")}>
            {name}
          </span>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{time}</span>
        </div>
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs text-muted-foreground truncate">{preview}</span>
          {conv.unreadCount > 0 && (
            <Badge className="h-4 min-w-[18px] px-1 text-[10px] bg-primary text-primary-foreground rounded-full flex-shrink-0">
              {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
            </Badge>
          )}
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {tags.slice(0, 3).map((tag) => {
              const c = tagClasses(tag.color);
              return (
                <span
                  key={tag.id}
                  className={cn("inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border", c.bg, c.text, c.border)}
                >
                  <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", c.dot)} />
                  {tag.name}
                </span>
              );
            })}
            {tags.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function ThreadHeader({
  conv,
  showInfo,
  onToggleInfo,
  onBack,
  humanActive,
  onReleaseToNya,
  releasingToNya,
}: {
  conv: Conversation;
  showInfo: boolean;
  onToggleInfo: () => void;
  onBack: () => void;
  humanActive: boolean;
  onReleaseToNya: () => void;
  releasingToNya: boolean;
}) {
  const name = getDisplayName(conv);
  return (
    <div className="flex-shrink-0 border-b bg-background">
      <div className="flex items-center gap-3 px-4 py-3">
        <Button variant="ghost" size="icon" className="h-8 w-8 lg:hidden" onClick={onBack}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div
          className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary flex-shrink-0"
        >
          {getInitials(name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-xs text-muted-foreground font-mono">{formatPhone(conv.phone)}</p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggleInfo}>
              {showInfo ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{showInfo ? "Hide contact info" : "Show contact info"}</TooltipContent>
        </Tooltip>
      </div>
      {humanActive && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 border-t border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <Bot className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-xs font-medium">Nya is paused: you're handling this conversation</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0"
            onClick={onReleaseToNya}
            disabled={releasingToNya}
          >
            {releasingToNya ? <Loader2 className="w-3 h-3 animate-spin" /> : "Hand back to Nya"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ComposeBar({
  value,
  onChange,
  onSend,
  sending,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  sending: boolean;
  disabled?: boolean;
}) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter = new line, Ctrl/Cmd+Enter = send
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!disabled && !sending && value.trim()) onSend();
    }
  };

  return (
    <div className="flex-shrink-0 border-t bg-background px-4 py-3">
      <div className="flex items-end gap-2">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? "Select a conversation to reply…" : "Type a message…"}
          disabled={disabled}
          rows={2}
          className="flex-1 resize-none text-sm min-h-[60px] max-h-[160px] rounded-xl"
        />
        <Button
          onClick={onSend}
          disabled={disabled || sending || !value.trim()}
          size="icon"
          className="h-10 w-10 rounded-xl flex-shrink-0"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </div>
      <p className="hidden sm:block text-[10px] text-muted-foreground mt-1.5">
        Enter for new line · Click send button to send
      </p>
    </div>
  );
}

function ContactInfoPanel({
  phone,
  contactInfo,
  onSendSignupLink,
  sendingLink,
  fullWidth = false,
  allTags,
  convTags,
  onAddTag,
  onRemoveTag,
  onOpenTagManager,
}: {
  phone: string;
  contactInfo?: ContactInfo;
  onSendSignupLink: () => void;
  sendingLink: boolean;
  fullWidth?: boolean;
  allTags: WaTag[];
  convTags: WaTag[];
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  onOpenTagManager: () => void;
}) {
  if (!contactInfo) {
    return (
      <div className={cn("flex-shrink-0 border-l flex items-center justify-center p-4", !fullWidth && "w-[280px]")}>
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const subColor = contactInfo.subscriptionStatus
    ? subStatusColors[contactInfo.subscriptionStatus] || "text-muted-foreground"
    : "text-muted-foreground";

  return (
    <div className={cn("flex flex-col bg-muted/20", fullWidth ? "flex-1" : "w-[280px] flex-shrink-0 border-l overflow-y-auto")}>
      {/* Header */}
      <div className="p-4 border-b bg-background">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
            {contactInfo.found && contactInfo.studentFirstName
              ? getInitials(`${contactInfo.studentFirstName} ${contactInfo.studentLastName || ""}`)
              : "?"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {contactInfo.found
                ? `${contactInfo.studentFirstName} ${contactInfo.studentLastName || ""}`
                : "Unknown Contact"}
            </p>
            <p className="text-xs text-muted-foreground font-mono">{formatPhone(phone)}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-4">
        {contactInfo.found ? (
          <>
            {/* Student info */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Student
              </p>
              <div className="space-y-2">
                <InfoRow
                  icon={<GraduationCap className="w-3.5 h-3.5" />}
                  label="Grade"
                  value={`Grade ${contactInfo.grade}`}
                />
                {contactInfo.school && (
                  <InfoRow
                    icon={<School className="w-3.5 h-3.5" />}
                    label="School"
                    value={contactInfo.school}
                  />
                )}
                {contactInfo.isActive !== undefined && (
                  <InfoRow
                    icon={
                      contactInfo.isActive ? (
                        <UserCheck className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <UserX className="w-3.5 h-3.5 text-muted-foreground" />
                      )
                    }
                    label="Activity"
                    value={
                      contactInfo.isActive
                        ? "Active (last 7 days)"
                        : contactInfo.daysSinceActive !== null && contactInfo.daysSinceActive !== undefined
                        ? `Inactive (${contactInfo.daysSinceActive}d ago)`
                        : "No activity"
                    }
                    valueClass={contactInfo.isActive ? "text-green-600" : "text-muted-foreground"}
                  />
                )}
                {contactInfo.isTestAccount && (
                  <InfoRow
                    icon={<Star className="w-3.5 h-3.5 text-amber-500" />}
                    label="Account type"
                    value="Test account"
                    valueClass="text-amber-600"
                  />
                )}
              </div>
            </section>

            <Separator />

            {/* Parent info */}
            {(contactInfo.parentFirstName || contactInfo.parentEmail) && (
              <>
                <section>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Parent
                  </p>
                  <div className="space-y-2">
                    {contactInfo.parentFirstName && (
                      <InfoRow
                        icon={<div className="w-3.5 h-3.5" />}
                        label="Name"
                        value={`${contactInfo.parentFirstName} ${contactInfo.parentLastName || ""}`}
                      />
                    )}
                    {contactInfo.parentEmail && (
                      <InfoRow
                        icon={<div className="w-3.5 h-3.5" />}
                        label="Email"
                        value={contactInfo.parentEmail}
                      />
                    )}
                  </div>
                </section>
                <Separator />
              </>
            )}

            {/* Subscription */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Subscription
              </p>
              <div className="space-y-2">
                <InfoRow
                  icon={<CreditCard className="w-3.5 h-3.5" />}
                  label="Status"
                  value={contactInfo.subscriptionStatus || "unknown"}
                  valueClass={subColor}
                />
                {contactInfo.subscriptionPlan && (
                  <InfoRow
                    icon={<div className="w-3.5 h-3.5" />}
                    label="Plan"
                    value={contactInfo.subscriptionPlan}
                  />
                )}
                {contactInfo.subscriptionEndDate && (
                  <InfoRow
                    icon={<Calendar className="w-3.5 h-3.5" />}
                    label={contactInfo.subscriptionExpired ? "Expired" : "Renews"}
                    value={format(new Date(contactInfo.subscriptionEndDate), "MMM d, yyyy")}
                    valueClass={contactInfo.subscriptionExpired ? "text-red-500" : undefined}
                  />
                )}
                {contactInfo.cancelAtPeriodEnd && (
                  <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-2 py-1.5 border border-amber-200">
                    Cancels at period end
                  </p>
                )}
              </div>
            </section>

            <Separator />

            {/* Actions */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Actions
              </p>
              <div className="space-y-2">
                {(contactInfo.subscriptionStatus === "inactive" ||
                  contactInfo.subscriptionStatus === "expired" ||
                  contactInfo.subscriptionStatus === "cancelled" ||
                  !contactInfo.subscriptionStatus) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={onSendSignupLink}
                    disabled={sendingLink}
                  >
                    {sendingLink ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Link2 className="w-3.5 h-3.5" />
                    )}
                    Send signup link
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 text-xs"
                  asChild
                >
                  <a
                    href={`/admin/users?openUser=${contactInfo.userId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View user profile
                  </a>
                </Button>
              </div>
            </section>
          </>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm font-medium mb-1">No linked account</p>
            <p className="text-xs">
              No EduFocal user is associated with this phone number.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 gap-2 text-xs"
              onClick={onSendSignupLink}
              disabled={sendingLink}
            >
              {sendingLink ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Link2 className="w-3.5 h-3.5" />
              )}
              Send signup link
            </Button>
          </div>
        )}

        {/* ── Tags ─────────────────────────────────────────────────────────── */}
        <Separator />
        <section>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Tags
            </p>
            <button
              onClick={onOpenTagManager}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Manage
            </button>
          </div>
          {/* Current tags */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {convTags.length === 0 && (
              <p className="text-xs text-muted-foreground">No tags yet</p>
            )}
            {convTags.map((tag) => {
              const c = tagClasses(tag.color);
              return (
                <span
                  key={tag.id}
                  className={cn("inline-flex items-center gap-1 text-xs font-medium pl-2 pr-1 py-0.5 rounded-full border", c.bg, c.text, c.border)}
                >
                  <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", c.dot)} />
                  {tag.name}
                  <button
                    onClick={() => onRemoveTag(tag.id)}
                    className="ml-0.5 hover:opacity-70 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
          {/* Add tag dropdown */}
          {allTags.filter(t => !convTags.some(ct => ct.id === t.id)).length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 w-full justify-start">
                  <Plus className="w-3 h-3" /> Add tag
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {allTags
                  .filter(t => !convTags.some(ct => ct.id === t.id))
                  .map(tag => {
                    const c = tagClasses(tag.color);
                    return (
                      <DropdownMenuItem key={tag.id} onClick={() => onAddTag(tag.id)} className="gap-2">
                        <span className={cn("w-2 h-2 rounded-full flex-shrink-0", c.dot)} />
                        {tag.name}
                      </DropdownMenuItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {allTags.length === 0 && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 w-full justify-start" onClick={onOpenTagManager}>
              <Plus className="w-3 h-3" /> Create your first tag
            </Button>
          )}
        </section>
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  valueClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <span className="text-xs text-muted-foreground">{label}: </span>
        <span className={cn("text-xs font-medium break-words", valueClass)}>{value}</span>
      </div>
    </div>
  );
}

// ─── Tag Manager Dialog ───────────────────────────────────────────────────────

function TagManagerDialog({
  open,
  onClose,
  tags,
  onCreate,
  onDelete,
  creating,
}: {
  open: boolean;
  onClose: () => void;
  tags: WaTag[];
  onCreate: (name: string, color: string) => void;
  onDelete: (id: string) => void;
  creating: boolean;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("blue");

  const handleCreate = () => {
    if (!name.trim()) return;
    onCreate(name.trim(), color);
    setName("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Tag className="w-4 h-4" /> Manage Tags
          </DialogTitle>
        </DialogHeader>

        {/* Create new tag */}
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Create tag</p>
          <Input
            placeholder="Tag name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            className="h-8 text-sm"
          />
          {/* Color picker */}
          <div className="flex flex-wrap gap-2">
            {TAG_COLOR_OPTIONS.map((c) => {
              const cls = tagClasses(c);
              return (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={cn(
                    "w-6 h-6 rounded-full border-2 transition-transform",
                    cls.dot,
                    color === c ? "border-foreground scale-110" : "border-transparent"
                  )}
                  title={c}
                />
              );
            })}
          </div>
          {/* Preview */}
          {name.trim() && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Preview:</span>
              <span className={cn("inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border", tagClasses(color).bg, tagClasses(color).text, tagClasses(color).border)}>
                <span className={cn("w-1.5 h-1.5 rounded-full", tagClasses(color).dot)} />
                {name.trim()}
              </span>
            </div>
          )}
          <Button
            size="sm"
            className="w-full gap-2"
            onClick={handleCreate}
            disabled={!name.trim() || creating}
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create tag
          </Button>
        </div>

        <Separator />

        {/* Existing tags */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Existing tags ({tags.length})
          </p>
          {tags.length === 0 && <p className="text-xs text-muted-foreground">No tags yet</p>}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {tags.map((tag) => {
              const c = tagClasses(tag.color);
              return (
                <div key={tag.id} className="flex items-center justify-between gap-2">
                  <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border", c.bg, c.text, c.border)}>
                    <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", c.dot)} />
                    {tag.name}
                  </span>
                  <button
                    onClick={() => onDelete(tag.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WhatsAppInbox() {
  const { toast } = useToast();

  const [activePhone, setActivePhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<WaMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [tagFilterId, setTagFilterId] = useState<string | null>(null);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activePhoneRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrolledMessageId = useRef<string | null>(null);

  // Keep ref in sync for SSE listener (avoids stale closure)
  useEffect(() => {
    activePhoneRef.current = activePhone;
  }, [activePhone]);

  // Conversations query
  const { data: conversations = [], refetch: refetchConversations } = useQuery<Conversation[]>({
    queryKey: ["/api/admin/whatsapp/conversations"],
    refetchInterval: 60_000,
  });

  // All tags
  const { data: allTags = [], refetch: refetchTags } = useQuery<WaTag[]>({
    queryKey: ["/api/admin/whatsapp/tags"],
  });

  // All conversation-tag mappings (phone → tagId[])
  const { data: convTagMappings = [], refetch: refetchConvTagMappings } = useQuery<{ phone: string; tagId: string }[]>({
    queryKey: ["/api/admin/whatsapp/conversation-tags"],
    refetchInterval: 60_000,
  });

  // Derived: tags for the active conversation
  const activeConvTags = useMemo(() => {
    if (!activePhone) return [];
    const canonical = activePhone.replace(/\D/g, '');
    const tagIds = convTagMappings.filter(m => m.phone === canonical).map(m => m.tagId);
    return allTags.filter(t => tagIds.includes(t.id));
  }, [activePhone, convTagMappings, allTags]);

  // Derived: tags per phone for conversation list
  const tagsByPhone = useMemo(() => {
    const map = new Map<string, WaTag[]>();
    for (const { phone, tagId } of convTagMappings) {
      const tag = allTags.find(t => t.id === tagId);
      if (!tag) continue;
      if (!map.has(phone)) map.set(phone, []);
      map.get(phone)!.push(tag);
    }
    return map;
  }, [convTagMappings, allTags]);

  // Tag mutations
  const createTagMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp/tags", { name, color });
      return res.json();
    },
    onSuccess: () => {
      refetchTags();
      toast({ title: "Tag created" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to create tag", description: err.message, variant: "destructive" });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/admin/whatsapp/tags/${id}`);
      return res.json();
    },
    onSuccess: () => {
      refetchTags();
      refetchConvTagMappings();
      if (tagFilterId) setTagFilterId(null);
      toast({ title: "Tag deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete tag", variant: "destructive" });
    },
  });

  const addTagMutation = useMutation({
    mutationFn: async ({ phone, tagId }: { phone: string; tagId: string }) => {
      const res = await apiRequest("POST", `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/tags/${tagId}`);
      return res.json();
    },
    onSuccess: () => refetchConvTagMappings(),
    onError: () => {
      toast({ title: "Failed to add tag", variant: "destructive" });
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: async ({ phone, tagId }: { phone: string; tagId: string }) => {
      const res = await apiRequest("DELETE", `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/tags/${tagId}`);
      return res.json();
    },
    onSuccess: () => refetchConvTagMappings(),
    onError: () => {
      toast({ title: "Failed to remove tag", variant: "destructive" });
    },
  });

  // Contact info for active conversation
  const { data: contactInfo } = useQuery<ContactInfo>({
    queryKey: ["/api/admin/whatsapp/contact-info", activePhone],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/admin/whatsapp/contact-info/${encodeURIComponent(activePhone!)}`
      );
      return res.json();
    },
    enabled: !!activePhone,
    staleTime: 60_000,
  });

  // Load messages for a conversation (initial load only: shows spinner)
  const loadMessages = useCallback(
    async (phone: string) => {
      lastScrolledMessageId.current = null;
      setMessagesLoading(true);
      try {
        const res = await apiRequest(
          "GET",
          `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/messages`
        );
        const data = await res.json();
        setMessages(Array.isArray(data) ? data : []);
      } catch {
        toast({ title: "Failed to load messages", variant: "destructive" });
      } finally {
        setMessagesLoading(false);
      }
    },
    [toast]
  );

  // Select a conversation
  const selectConversation = useCallback(
    async (phone: string) => {
      if (phone === activePhoneRef.current) return;
      setActivePhone(phone);
      setMessages([]);
      setReply("");
      await loadMessages(phone);
      // Mark as read (fire-and-forget)
      apiRequest(
        "POST",
        `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/mark-read`
      )
        .then(() => refetchConversations())
        .catch(() => {});
    },
    [loadMessages, refetchConversations]
  );

  // Auto-scroll to bottom only when a genuinely new message arrives
  useEffect(() => {
    if (messages.length === 0) return;
    const sorted = [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    const lastId = sorted[sorted.length - 1].id;
    if (lastId !== lastScrolledMessageId.current) {
      lastScrolledMessageId.current = lastId;
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Silent polling: keeps chat in sync as a fallback alongside SSE
  useEffect(() => {
    if (!activePhone) return;
    lastScrolledMessageId.current = null;
    const poll = async () => {
      try {
        const res = await apiRequest(
          "GET",
          `/api/admin/whatsapp/conversations/${encodeURIComponent(activePhone)}/messages`
        );
        const data = await res.json();
        if (Array.isArray(data)) setMessages(data);
      } catch { /* silent */ }
    };
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [activePhone]);

  // SSE connection with auto-reconnect
  useEffect(() => {
    let destroyed = false;

    const connect = () => {
      if (destroyed) return;
      const es = new EventSource("/api/admin/whatsapp/stream");
      eventSourceRef.current = es;

      es.onopen = () => {
        if (!destroyed) setSseConnected(true);
      };

      es.onerror = () => {
        if (destroyed) return;
        setSseConnected(false);
        es.close();
        reconnectTimeout.current = setTimeout(connect, 5000);
      };

      es.addEventListener("message", (e) => {
        if (destroyed) return;
        try {
          const { phone, message } = JSON.parse(e.data) as { phone: string; message: WaMessage };
          // Refresh conversation list to update last message + unread
          refetchConversations();
          // Add to active thread if it matches
          if (phone === activePhoneRef.current) {
            setMessages((prev) => {
              if (prev.find((m) => m.id === message.id)) return prev;
              return [...prev, message];
            });
          }
        } catch {}
      });
    };

    connect();

    return () => {
      destroyed = true;
      eventSourceRef.current?.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    };
  }, [refetchConversations]);

  // Takeover status query
  const { data: takeoverStatus, refetch: refetchTakeover } = useQuery<{ active: boolean }>({
    queryKey: ["/api/admin/whatsapp/takeover-status", activePhone],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/admin/whatsapp/conversations/${encodeURIComponent(activePhone!)}/takeover-status`
      );
      return res.json();
    },
    enabled: !!activePhone,
    refetchInterval: 30_000,
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: async ({ phone, message }: { phone: string; message: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/reply`,
        { message }
      );
      return res.json();
    },
    onSuccess: () => {
      // Don't reload messages here: the server pushes the sent message via SSE,
      // and the silent poll picks it up within 5s. Reloading causes the scroll-to-top jump.
      refetchConversations();
      refetchTakeover();
    },
    onError: (err: Error) => {
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    },
  });

  // Release to Nya mutation
  const releaseToNyaMutation = useMutation({
    mutationFn: async (phone: string) => {
      const res = await apiRequest(
        "DELETE",
        `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/takeover`
      );
      return res.json();
    },
    onSuccess: () => {
      refetchTakeover();
      toast({ title: "Handed back to Nya", description: "Nya will respond to the next message." });
    },
    onError: () => {
      toast({ title: "Failed to release", variant: "destructive" });
    },
  });

  // Signup link mutation
  const signupLinkMutation = useMutation({
    mutationFn: async (phone: string) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/whatsapp/conversations/${encodeURIComponent(phone)}/send-signup-link`,
        {}
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Signup link sent" });
      if (activePhoneRef.current) loadMessages(activePhoneRef.current);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to send link", description: err.message, variant: "destructive" });
    },
  });

  const handleSend = () => {
    if (!activePhone || !reply.trim()) return;
    replyMutation.mutate({ phone: activePhone, message: reply.trim() });
    setReply("");
  };

  // Filtered conversations
  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (tagFilterId) {
      list = list.filter((c) => {
        const canonical = c.phone.replace(/\D/g, '');
        return convTagMappings.some(m => m.phone === canonical && m.tagId === tagFilterId);
      });
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (c) =>
        c.phone.includes(q) ||
        (c.displayName || "").toLowerCase().includes(q) ||
        (c.lastMessage.messageBody || "").toLowerCase().includes(q)
    );
  }, [conversations, searchQuery, tagFilterId, convTagMappings]);

  const activeConv = conversations.find((c) => c.phone === activePhone);

  return (
    <TooltipProvider>
      <div className="flex h-full overflow-hidden bg-background">
        {/* ── Left panel: Conversation list ─────────────────────────────── */}
        <div
          className={cn(
            "flex-shrink-0 border-r flex flex-col w-full lg:w-[300px]",
            // On mobile, hide when a conversation is active
            activePhone && "hidden lg:flex"
          )}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <MessageSquare className="w-4.5 h-4.5 text-primary flex-shrink-0" />
            <span className="font-semibold text-sm flex-1">WhatsApp Inbox</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0",
                    sseConnected ? "bg-green-500" : "bg-amber-400"
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>
                {sseConnected ? "Live: real-time updates active" : "Reconnecting…"}
              </TooltipContent>
            </Tooltip>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => refetchConversations()}
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Search */}
          <div className="px-3 py-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search conversations…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>

          {/* Stats row */}
          {conversations.length > 0 && (
            <div className="px-3 py-1.5 border-b bg-muted/30 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{filteredConversations.length}{tagFilterId || searchQuery ? ` of ${conversations.length}` : ""} conversations</span>
              {conversations.some((c) => c.unreadCount > 0) && (
                <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                  {conversations.reduce((acc, c) => acc + c.unreadCount, 0)} unread
                </Badge>
              )}
              <button
                className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setTagManagerOpen(true)}
                title="Manage tags"
              >
                <Tag className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Tag filter pills */}
          {allTags.length > 0 && (
            <div className="px-3 py-2 border-b flex flex-wrap gap-1.5 bg-muted/10">
              {tagFilterId && (
                <button
                  onClick={() => setTagFilterId(null)}
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-background border-border text-muted-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-2.5 h-2.5" /> Clear filter
                </button>
              )}
              {allTags.map((tag) => {
                const c = tagClasses(tag.color);
                const active = tagFilterId === tag.id;
                return (
                  <button
                    key={tag.id}
                    onClick={() => setTagFilterId(active ? null : tag.id)}
                    className={cn(
                      "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border transition-all",
                      active ? cn(c.bg, c.text, c.border, "ring-1 ring-current") : "bg-background border-border text-muted-foreground hover:bg-muted"
                    )}
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", active ? c.dot : "bg-muted-foreground")} />
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <MessageSquare className="w-7 h-7 mb-2 opacity-30" />
                <p className="text-xs">
                  {searchQuery || tagFilterId ? "No matches" : "No conversations yet"}
                </p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const canonical = conv.phone.replace(/\D/g, '');
                const convTags = tagsByPhone.get(canonical) ?? [];
                return (
                  <ConversationItem
                    key={conv.phone}
                    conv={conv}
                    active={conv.phone === activePhone}
                    onClick={() => selectConversation(conv.phone)}
                    tags={convTags}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* ── Center panel: Message thread ───────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {!activePhone ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
              <MessageSquare className="w-14 h-14 opacity-20" />
              <p className="text-sm">Select a conversation to start</p>
            </div>
          ) : (
            <>
              {activeConv && (
                <ThreadHeader
                  conv={activeConv}
                  showInfo={showContactInfo}
                  onToggleInfo={() => setShowContactInfo((v) => !v)}
                  onBack={() => setActivePhone(null)}
                  humanActive={takeoverStatus?.active ?? false}
                  onReleaseToNya={() => activePhone && releaseToNyaMutation.mutate(activePhone)}
                  releasingToNya={releaseToNyaMutation.isPending}
                />
              )}

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-4 py-2">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <MessageList messages={messages} />
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose bar */}
              <ComposeBar
                value={reply}
                onChange={setReply}
                onSend={handleSend}
                sending={replyMutation.isPending}
              />
            </>
          )}
        </div>

        {/* ── Right panel: Contact info ──────────────────────────────────── */}
        {activePhone && showContactInfo && (
          <>
            {/* Mobile: full-screen overlay */}
            <div className="lg:hidden fixed inset-0 z-[100] bg-background flex flex-col">
              {/* Sticky back header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-background flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowContactInfo(false)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-semibold">Contact Info</span>
              </div>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto">
                <ContactInfoPanel
                  phone={activePhone}
                  contactInfo={contactInfo}
                  onSendSignupLink={() => signupLinkMutation.mutate(activePhone)}
                  sendingLink={signupLinkMutation.isPending}
                  fullWidth
                  allTags={allTags}
                  convTags={activeConvTags}
                  onAddTag={(tagId) => addTagMutation.mutate({ phone: activePhone, tagId })}
                  onRemoveTag={(tagId) => removeTagMutation.mutate({ phone: activePhone, tagId })}
                  onOpenTagManager={() => setTagManagerOpen(true)}
                />
              </div>
            </div>
            {/* Desktop: sidebar */}
            <div className="hidden lg:flex">
              <ContactInfoPanel
                phone={activePhone}
                contactInfo={contactInfo}
                onSendSignupLink={() => signupLinkMutation.mutate(activePhone)}
                sendingLink={signupLinkMutation.isPending}
                allTags={allTags}
                convTags={activeConvTags}
                onAddTag={(tagId) => addTagMutation.mutate({ phone: activePhone, tagId })}
                onRemoveTag={(tagId) => removeTagMutation.mutate({ phone: activePhone, tagId })}
                onOpenTagManager={() => setTagManagerOpen(true)}
              />
            </div>
          </>
        )}
      </div>

      {/* Tag Manager Dialog */}
      <TagManagerDialog
        open={tagManagerOpen}
        onClose={() => setTagManagerOpen(false)}
        tags={allTags}
        onCreate={(name, color) => createTagMutation.mutate({ name, color })}
        onDelete={(id) => deleteTagMutation.mutate(id)}
        creating={createTagMutation.isPending}
      />
    </TooltipProvider>
  );
}
